import type { BillingCatalog } from '../client';
import type { BillingCycle, PlanInfo } from '@auw/workspace-utils';

export const DEFAULT_BILLING_CURRENCY = 'USD';

export interface ResolvedPlanPricing {
  monthlyAmount: number;
  yearlyTotal: number;
  yearlyMonthlyEquivalent: number;
  currencyCode: string;
}

export function formatCurrencyAmount(amount: number, currencyCode: string, locale?: string): string {
  const normalizedCurrency = String(currencyCode || DEFAULT_BILLING_CURRENCY).trim().toUpperCase() || DEFAULT_BILLING_CURRENCY;
  const maximumFractionDigits = Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits,
    }).format(amount);
  } catch {
    return `${normalizedCurrency} ${amount % 1 === 0 ? amount : amount.toFixed(2)}`;
  }
}

export function resolvePlanPricing(plan: PlanInfo, catalog: BillingCatalog | null | undefined): ResolvedPlanPricing {
  const planCatalog = catalog?.plans?.[plan.type];
  const monthlyCatalog = planCatalog?.monthly ?? null;
  const yearlyCatalog = planCatalog?.yearly ?? null;

  const fallbackMonthly = plan.monthlyPrice > 0 ? plan.monthlyPrice : 0;
  const monthlyAmount = typeof monthlyCatalog?.amount === 'number' ? monthlyCatalog.amount : fallbackMonthly;
  const fallbackYearlyTotal = fallbackMonthly > 0 ? Math.round(fallbackMonthly * 0.84 * 12) : 0;
  const fallbackYearlyMonthlyEquivalent = fallbackMonthly > 0 ? +(fallbackMonthly * 0.84).toFixed(2) : 0;

  return {
    monthlyAmount,
    yearlyTotal: typeof yearlyCatalog?.amount === 'number' ? yearlyCatalog.amount : fallbackYearlyTotal,
    yearlyMonthlyEquivalent: typeof yearlyCatalog?.monthly_equivalent === 'number'
      ? yearlyCatalog.monthly_equivalent
      : fallbackYearlyMonthlyEquivalent,
    currencyCode: monthlyCatalog?.currency_code ?? yearlyCatalog?.currency_code ?? DEFAULT_BILLING_CURRENCY,
  };
}

export function resolveCycleAmount(pricing: ResolvedPlanPricing, cycle: BillingCycle): number {
  if (cycle === 'yearly' && pricing.yearlyTotal > 0) {
    return pricing.yearlyTotal;
  }
  return pricing.monthlyAmount;
}
